import { useEffect, useRef, useState } from "react";
import Signature from "../types/Signature";
import { SignatureType } from "../common/enums";
import { showDangerDialog } from "../common";
import Resources from "../types/Resources";
import { String_base64StringToBlob } from "../common/stringUtils";
import { getSignatureBlob } from "../apiClient";
import BaseUserInfo from "../types/BaseUserInfo";

type UseSignatureProps = {
    signature: Signature;
    resources: Resources;
    includePhoneNumber?: boolean;
    onSaveSignature: (
        signatureBlob: Blob,
        nameClarification: string,
        signatureType: SignatureType,
        phoneNumber?: string
    ) => Promise<void>;
    baseUserInfo: BaseUserInfo;
    hideSignature?: boolean;
    disableSignButton?: boolean;
    signButtonTooltip?: string;
    onRemoveSignature: (signature: Signature) => Promise<void>;
    hideSignatureModalEventListener?: () => void;
    showSignatureModalEventListener?: () => void;
};

export type SignatureManager = {
    signatureUrl: string;
    showSignatureModal: boolean;
    nameClarificationInput: string;
    phoneNumberInput: string;
    errorMessage: string;
    signaturePadRef: any;
    signature: Signature;
    isLoading: boolean;
    hideSignature: boolean;
    includePhoneNumber: boolean;
    disableSignButton: boolean;
    signButtonTooltip: string;

    removeSignatureButtonClickHandlerAsync: () => Promise<void>;
    signButtonClickHandler: () => void;
    saveSignatureButtonClickHandlerAsync: () => Promise<void>;
    setShowSignatureModal: (show: boolean) => void;
    setNameClarificationInput: (nameClarification: string) => void;
    setPhoneNumberInput: (phoneNumber: string) => void;
    clearSignatureCanvasButtonClickHandler: () => void;
    hideSignatureModalHandler: () => void;
};

export default function useSignature({
    signature,
    resources,
    includePhoneNumber = false,
    onSaveSignature,
    onRemoveSignature,
    hideSignatureModalEventListener,
    showSignatureModalEventListener,
    baseUserInfo,
    hideSignature = false,
    disableSignButton = false,
    signButtonTooltip = ""
}: UseSignatureProps): SignatureManager {
    const [signatureUrl, setSignatureUrl] = useState<string | undefined>(undefined);
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [nameClarificationInput, setNameClarificationInput] = useState("");
    const [phoneNumberInput, setPhoneNumberInput] = useState("");
    const [errorMessage, setErrorMessage] = useState<string>();
    const [isLoading, setIsLoading] = useState(false);

    const signaturePadRef = useRef<any>(null);

    useEffect(() => {
        async function getBlob() {
            let blob: Blob;

            if (signature.id) {
                const getBlobResult = await getSignatureBlob(signature.id);

                if (getBlobResult.failed) {
                    console.error(getBlobResult);

                    if (getBlobResult.error.status !== 404) {
                        showDangerDialog(resources.get("SomethingWentWrong"), resources.get("GetSignatureError"));
                    }

                    return;
                }

                blob = getBlobResult.value;
            } else {
                try {
                    blob = String_base64StringToBlob(signature.base64Signature);
                } catch (error) {
                    console.error(error);
                    showDangerDialog(resources.get("SomethingWentWrong"), resources.get("GetSignatureError"));
                    return;
                }
            }

            const signatureUrl: string = URL.createObjectURL(blob);
            setSignatureUrl(signatureUrl);
        }

        if (!signature) {
            console.error("No signature was provided.");
            return;
        }

        if (!signature.id && !signature.base64Signature) {
            return;
        }

        getBlob();
    }, [signature.id, signature.base64Signature]);

    useEffect(() => {
        if (signatureUrl && !signature.id && !signature.base64Signature) {
            URL.revokeObjectURL(signatureUrl);
            setSignatureUrl(undefined);
        }

        return () => {
            if (signatureUrl) {
                URL.revokeObjectURL(signatureUrl);
            }
        };
    }, [signature.id, signature.base64Signature]);

    async function removeSignatureButtonClickHandlerAsync() {
        if (isLoading) {
            console.error("Attempted to remove a signature while another operation is in progress.");
            return;
        }
        setIsLoading(true);

        if (signatureUrl) {
            URL.revokeObjectURL(signatureUrl);
            setSignatureUrl(undefined);
        }

        await onRemoveSignature(signature);

        setIsLoading(false);
    }

    async function saveSignatureButtonClickHandlerAsync() {
        if (isLoading) {
            console.error("Attempted to save a signature while another operation is in progress.");
            return;
        }

        setIsLoading(true);

        const signaturePad = signaturePadRef.current;

        if (!signaturePad) {
            console.error("SignaturePad is not initialized.");
            showDangerDialog(resources.get("SomethingWentWrong"), resources.get("SaveSignatureError"));
            setIsLoading(false);
            return;
        }

        setErrorMessage("");

        if (signaturePad.isEmpty()) {
            setErrorMessage(resources.get("MustProvideSignature"));
            setIsLoading(false);
            return;
        }

        if (!nameClarificationInput) {
            setErrorMessage(resources.get("MustProvideNameClarification"));
            setIsLoading(false);
            return;
        }

        if (includePhoneNumber && !phoneNumberInput) {
            setErrorMessage(resources.get("MustProvidePhoneNumber"));
            setIsLoading(false);
            return;
        }

        prepareSignatureBackground(signaturePad.canvasRef.current);

        const base64String = compressCanvasData(signaturePad.canvasRef.current);
        const blob = String_base64StringToBlob(base64String);
        const signatureUrl: string = URL.createObjectURL(blob);
        setSignatureUrl(signatureUrl);

        hideSignatureModalHandler();

        await onSaveSignature(blob, nameClarificationInput, signature.signatureType, phoneNumberInput);

        setIsLoading(false);
    }

    function signButtonClickHandler() {
        showSignatureModalHandler();
    }

    function showSignatureModalHandler() {
        setShowSignatureModal(true);
        setNameClarificationInput(baseUserInfo.fullName);

        if (showSignatureModalEventListener) {
            showSignatureModalEventListener();
        }
    }

    function hideSignatureModalHandler() {
        setShowSignatureModal(false);

        if (hideSignatureModalEventListener) {
            hideSignatureModalEventListener();
        }
    }

    function clearSignatureCanvasButtonClickHandler() {
        const signaturePad = signaturePadRef.current;
        if (!signaturePad) {
            console.error("SignaturePad is not initialized.");
            return;
        }

        signaturePad.clear();
    }

    return {
        signatureUrl: signatureUrl ?? "",
        showSignatureModal,
        nameClarificationInput,
        phoneNumberInput,
        errorMessage: errorMessage ?? "",
        signaturePadRef,
        signature,
        isLoading,
        hideSignature,
        includePhoneNumber,
        disableSignButton,
        signButtonTooltip,

        removeSignatureButtonClickHandlerAsync,
        saveSignatureButtonClickHandlerAsync,
        signButtonClickHandler,
        setShowSignatureModal,
        setNameClarificationInput,
        setPhoneNumberInput,
        clearSignatureCanvasButtonClickHandler,
        hideSignatureModalHandler
    };
}

function prepareSignatureBackground(canvas: HTMLCanvasElement) {
    const context = canvas.getContext("2d");

    if (!context) {
        console.error("Failed to get canvas context.");
        return;
    }

    const imgData = context.getImageData(0, 0, canvas.width, canvas.height);
    const data = imgData.data;
    for (var i = 0; i < data.length; i += 4) {
        if (data[i + 3] === 0) {
            data[i] = 255;
            data[i + 1] = 255;
            data[i + 2] = 255;
            data[i + 3] = 255;
        }
    }
    context.putImageData(imgData, 0, 0);
}

const MAX_SIGNATURE_SIZE = 20_000;
function compressCanvasData(canvas: HTMLCanvasElement) {
    let data = canvas.toDataURL("image/jpeg");
    let quality = 0.8;

    while (quality > 0 && data.length > MAX_SIGNATURE_SIZE) {
        data = canvas.toDataURL("image/jpeg", quality);
        quality -= 0.2;
    }

    return data;
}
